'use client';

import React, { useState } from 'react';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { motion } from 'framer-motion';
import { Users, DollarSign, Target, Upload, CheckCircle } from 'lucide-react';

type FormValues = {
  fullName: string;
  email: string;
  website?: string;
  twitterHandle?: string;
  instagramHandle?: string;
  youtubeHandle?: string;
  tiktokHandle?: string;
  followersTwitter?: number;
  followersInstagram?: number;
  followersYoutube?: number;
  followersTiktok?: number;
  promotionPlan: string;
  previousExperience?: string;
  serviceIndustryExperience?: string;
  audienceType: string;
};

const AffiliateForm: React.FC = () => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>();
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const router = useRouter();

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setLoading(true);
    try {
      const response = await fetch('/api/affiliates/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setShowSuccess(true);
        
        // Redirect after showing success
        setTimeout(() => {
          router.push('/');
        }, 5000);
      } else {
        alert(result.message || 'There was an error submitting your application. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting affiliate application:', error);
      alert('There was an error submitting your application. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (showSuccess) {
    return (
      <main id="main-content" className="min-h-screen flex items-center justify-center bg-white">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md mx-auto text-center p-8 rounded-2xl bg-white border border-primary/10 shadow-xl"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="w-8 h-8 text-green-600" />
          </motion.div>
          <h1 className="text-2xl font-bold text-black mb-4">Application Submitted! 🎉</h1>
          <p className="text-gray-600 mb-6">
            Thanks for applying to our affiliate program! We'll review your application and follow up by email.
          </p>
          <p className="text-sm text-gray-500">Redirecting you to the homepage...</p>
        </motion.div>
      </main>
    );
  }

  return (
    <main id="main-content" className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative pt-40 pb-16 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-[linear-gradient(45deg,rgba(46,139,87,0.02)_1px,transparent_1px),linear-gradient(to_right,rgba(46,139,87,0.02)_1px,transparent_1px)] bg-[size:4rem_4rem] [transform:perspective(1000px)_rotateX(60deg)] opacity-20" />
          <motion.div
            className="absolute top-20 left-1/4 w-[300px] h-[300px] rounded-full bg-gradient-to-br from-primary/5 to-primary/10 blur-[80px]"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-6 py-2 rounded-full bg-primary/5 text-primary border border-primary/20 shadow-lg shadow-primary/5 mb-8"
          >
            <Users className="w-4 h-4" />
            <span className="text-sm font-medium">Affiliate Program</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-black mb-6"
          >
            Partner with{' '}
            <span className="inline-block bg-gradient-to-r from-black via-primary to-black bg-clip-text text-transparent bg-[length:200%_auto] animate-text-shimmer">
              TipKeepr
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl sm:text-2xl text-gray-600 mb-12 leading-relaxed"
          >
            Earn commissions promoting professional tip tracking tools to the service industry. Perfect for content creators who understand restaurant and bar work. 🍸
          </motion.p>

          <h2 className="sr-only">Affiliate program benefits</h2>
          {/* Benefits Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16"
          >
            <div className="p-6 rounded-xl bg-white border border-primary/10 hover:shadow-lg transition-all duration-300">
              <DollarSign className="w-8 h-8 text-primary mb-4 mx-auto" />
              <h3 className="font-semibold text-black mb-2">Referral Commissions</h3>
              <p className="text-gray-600 text-sm">Approved partners receive current commission details</p>
            </div>
            <div className="p-6 rounded-xl bg-white border border-primary/10 hover:shadow-lg transition-all duration-300">
              <Target className="w-8 h-8 text-primary mb-4 mx-auto" />
              <h3 className="font-semibold text-black mb-2">Service Industry Focus</h3>
              <p className="text-gray-600 text-sm">Perfect for restaurant & bar audiences</p>
            </div>
            <div className="p-6 rounded-xl bg-white border border-primary/10 hover:shadow-lg transition-all duration-300">
              <Users className="w-8 h-8 text-primary mb-4 mx-auto" />
              <h3 className="font-semibold text-black mb-2">Marketing Support</h3>
              <p className="text-gray-600 text-sm">Resources & guidance to help you succeed</p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Application Form */}
      <div className="max-w-3xl mx-auto px-4 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl border border-primary/10 shadow-xl p-8"
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-black mb-4">Apply to Join</h2>
            <p className="text-gray-600">Fill out the form below and our team will follow up by email after review.</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Personal Information */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-black border-b border-gray-200 pb-2">Personal Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register('fullName', { required: 'Full name is required' })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
                    placeholder="Your full name"
                  />
                  {errors.fullName && <span className="text-red-500 text-sm mt-1">{errors.fullName.message}</span>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: 'Invalid email address',
                      },
                    })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
                    placeholder="your@email.com"
                  />
                  {errors.email && <span className="text-red-500 text-sm mt-1">{errors.email.message}</span>}
                </div>
              </div>
            </div>

            {/* Online Presence */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-black border-b border-gray-200 pb-2">Online Presence</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Website/Blog URL</label>
                  <input
                    type="url"
                    {...register('website', {
                      pattern: {
                        value: /^(https?:\/\/)?([\w\d-]+\.){1,}([a-zA-Z]{2,})(\/.*)?$/,
                        message: 'Invalid URL',
                      },
                    })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
                    placeholder="https://yourwebsite.com"
                  />
                  {errors.website && <span className="text-red-500 text-sm mt-1">{errors.website.message}</span>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Twitter Handle</label>
                  <input
                    type="text"
                    {...register('twitterHandle')}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
                    placeholder="@yourhandle"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Instagram Handle</label>
                  <input
                    type="text"
                    {...register('instagramHandle')}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
                    placeholder="@yourhandle"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">TikTok Handle</label>
                  <input
                    type="text"
                    {...register('tiktokHandle')}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
                    placeholder="@yourhandle"
                  />
                </div>
              </div>
            </div>

            {/* Audience Metrics */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-black border-b border-gray-200 pb-2">Audience Metrics</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Twitter Followers</label>
                  <input
                    type="number"
                    {...register('followersTwitter', { min: { value: 0, message: 'Cannot be negative' } })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
                    placeholder="1000"
                  />
                  {errors.followersTwitter && <span className="text-red-500 text-sm mt-1">{errors.followersTwitter.message}</span>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Instagram Followers</label>
                  <input
                    type="number"
                    {...register('followersInstagram', { min: { value: 0, message: 'Cannot be negative' } })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
                    placeholder="5000"
                  />
                  {errors.followersInstagram && <span className="text-red-500 text-sm mt-1">{errors.followersInstagram.message}</span>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">TikTok Followers</label>
                  <input
                    type="number"
                    {...register('followersTiktok', { min: { value: 0, message: 'Cannot be negative' } })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
                    placeholder="10000"
                  />
                  {errors.followersTiktok && <span className="text-red-500 text-sm mt-1">{errors.followersTiktok.message}</span>}
                </div>

                <div>
                  <label htmlFor="audienceType" className="block text-sm font-medium text-gray-700 mb-2">Primary Audience Type <span className="text-red-500">*</span></label>
                  <select
                    id="audienceType"
                    {...register('audienceType', { required: 'Please select your audience type' })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
                  >
                    <option value="">Select audience type</option>
                    <option value="service-workers">Service Industry Workers</option>
                    <option value="restaurant-owners">Restaurant/Bar Owners</option>
                    <option value="business-entrepreneurs">Business/Entrepreneurship</option>
                    <option value="lifestyle-general">Lifestyle/General</option>
                    <option value="finance-career">Finance/Career</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.audienceType && <span className="text-red-500 text-sm mt-1">{errors.audienceType.message}</span>}
                </div>
              </div>
            </div>

            {/* Promotion Plan */}
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-black border-b border-gray-200 pb-2">Promotion Strategy</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  How do you plan to promote TipKeepr? <span className="text-red-500">*</span>
                </label>
                <textarea
                  {...register('promotionPlan', { required: 'Please describe your promotion plan' })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
                  rows={5}
                  placeholder="Describe your content strategy, target audience, and how you'll showcase TipKeepr to your followers..."
                />
                {errors.promotionPlan && <span className="text-red-500 text-sm mt-1">{errors.promotionPlan.message}</span>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Service Industry Experience</label>
                <textarea
                  {...register('serviceIndustryExperience')}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
                  rows={4}
                  placeholder="Have you worked in restaurants, bars, or other service businesses? This helps us understand your connection to our target audience..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Previous Affiliate Experience</label>
                <textarea
                  {...register('previousExperience')}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
                  rows={4}
                  placeholder="Tell us about any previous affiliate marketing or brand partnership experience..."
                />
              </div>
            </div>

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={loading}
              whileHover={{ scale: loading ? 1 : 1.02 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              className={`w-full py-4 rounded-xl text-lg font-medium transition-all duration-300 ${
                loading
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-black text-white hover:bg-black/90 hover:shadow-lg'
              }`}
            >
              {loading ? 'Submitting Application...' : 'Submit Application'}
            </motion.button>
          </form>
        </motion.div>
      </div>
    </main>
  );
};

export default AffiliateForm;
